import React, { useState } from 'react';
import { Button } from 'antd';
import { CheckCircle, Circle, User, Lightning, Buildings, AddressBook, FileText, CreditCard, CheckSquare, Square } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import './oneButton-css/FinancePartnerSyncModal.css';
import DashboardModal from './DashboardModal';

export type LeadSyncAction = 'create' | 'sync_existing';

export interface FinancePartnerSyncModalProps {
    open: boolean;
    onClose: () => void;
    /** Callback when user completes or acknowledges the checklist (e.g. to close and persist) */
    onComplete?: () => void;
}

/** Main checklist state for the sync pop-up (Lead Sync step, Automation Checks, Additional Syncs) */
export interface SyncChecklistState {
    leadSyncAction: LeadSyncAction | null;
    leadSystemSync: boolean;
    utilitySync: boolean;
    contactSync: boolean;
    titleCheck: boolean;
    creditCheck: boolean;
}

const initialChecklist: SyncChecklistState = {
    leadSyncAction: null,
    leadSystemSync: false,
    utilitySync: false,
    contactSync: false,
    titleCheck: false,
    creditCheck: false,
};

export const FinancePartnerSyncModal: React.FC<FinancePartnerSyncModalProps> = ({
    open,
    onClose,
    onComplete,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const [checklist, setChecklist] = useState<SyncChecklistState>(initialChecklist);
    const setLeadSyncFormOpen = useOneButtonStore((s) => s.setLeadSyncFormOpen);

    const handleLeadSyncChoice = (action: LeadSyncAction) => {
        setChecklist((c) => ({ ...c, leadSyncAction: action }));
        setLeadSyncFormOpen(true, action, null);
    };

    const toggle = (key: keyof Omit<SyncChecklistState, 'leadSyncAction'>) => {
        setChecklist((c) => ({ ...c, [key]: !c[key] }));
    };

    const handleDone = () => {
        onComplete?.();
        onClose();
    };

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            width={520}
            maskClosable={false}
            className="obd-sync-modal"
            rootClassName="obd-sync-modal-root"
            theme={theme}
            title="Finance Partner Sync"
            footer={(
                <Button type="primary" onClick={handleDone}>
                    Done
                </Button>
            )}
        >
            <div style={{ position: 'relative' }} className={`obd-theme-${theme}`}>
                <p className="obd-sync-modal__subtitle">
                    Complete the steps below when Lead, Lead System, or Utility sync has not occurred or does not exist.
                </p>

                {/* 1. Lead Sync — checkboxes for new lead vs sync existing */}
                <section className="obd-sync-modal__section">
                    <h4 className="obd-sync-modal__section-title">1. Lead Sync</h4>
                    <div className="obd-sync-modal__options obd-sync-modal__options--checkbox">
                        <button
                            type="button"
                            className={`obd-sync-modal__option obd-sync-modal__option--checkbox ${checklist.leadSyncAction === 'create' ? 'obd-sync-modal__option--selected' : ''}`}
                            onClick={() => handleLeadSyncChoice('create')}
                            title="Create a new lead and open sync form"
                        >
                            <span className="obd-sync-modal__checkbox-icon" aria-hidden>
                                {checklist.leadSyncAction === 'create' ? <CheckSquare size={20} weight="fill" /> : <Square size={20} weight="regular" />}
                            </span>
                            <User size={18} weight="duotone" className="obd-sync-modal__option-icon" />
                            <span>Create a new lead</span>
                        </button>
                        <button
                            type="button"
                            className={`obd-sync-modal__option obd-sync-modal__option--checkbox ${checklist.leadSyncAction === 'sync_existing' ? 'obd-sync-modal__option--selected' : ''}`}
                            onClick={() => handleLeadSyncChoice('sync_existing')}
                            title="Sync with an existing lead and open form"
                        >
                            <span className="obd-sync-modal__checkbox-icon" aria-hidden>
                                {checklist.leadSyncAction === 'sync_existing' ? <CheckSquare size={20} weight="fill" /> : <Square size={20} weight="regular" />}
                            </span>
                            <User size={18} weight="duotone" className="obd-sync-modal__option-icon" />
                            <span>Sync with an existing lead</span>
                        </button>
                    </div>
                </section>

                {/* 2. Automation Checks */}
                <section className="obd-sync-modal__section">
                    <h4 className="obd-sync-modal__section-title">2. Automation Checks</h4>
                    <ul className="obd-sync-modal__checklist">
                        <li className="obd-sync-modal__check-item">
                            <button type="button" className="obd-sync-modal__check-row" onClick={() => toggle('utilitySync')}>
                                {checklist.utilitySync ? <CheckCircle size={20} weight="fill" className="obd-sync-modal__check-icon obd-sync-modal__check-icon--done" /> : <Circle size={20} weight="regular" className="obd-sync-modal__check-icon" />}
                                <Lightning size={18} weight="duotone" aria-hidden />
                                <span>Utility sync</span>
                            </button>
                        </li>
                        <li className="obd-sync-modal__check-item">
                            <button type="button" className="obd-sync-modal__check-row" onClick={() => toggle('leadSystemSync')}>
                                {checklist.leadSystemSync ? <CheckCircle size={20} weight="fill" className="obd-sync-modal__check-icon obd-sync-modal__check-icon--done" /> : <Circle size={20} weight="regular" className="obd-sync-modal__check-icon" />}
                                <Buildings size={18} weight="duotone" aria-hidden />
                                <span>Lead System sync</span>
                            </button>
                        </li>
                    </ul>
                </section>

                {/* 3. Additional Required Syncs */}
                <section className="obd-sync-modal__section">
                    <h4 className="obd-sync-modal__section-title">3. Additional Required Syncs</h4>
                    <ul className="obd-sync-modal__checklist">
                        <li className="obd-sync-modal__check-item">
                            <button type="button" className="obd-sync-modal__check-row" onClick={() => toggle('contactSync')}>
                                {checklist.contactSync ? <CheckCircle size={20} weight="fill" className="obd-sync-modal__check-icon obd-sync-modal__check-icon--done" /> : <Circle size={20} weight="regular" className="obd-sync-modal__check-icon" />}
                                <AddressBook size={18} weight="duotone" aria-hidden />
                                <span>Contact sync</span>
                            </button>
                        </li>
                        <li className="obd-sync-modal__check-item">
                            <button type="button" className="obd-sync-modal__check-row" onClick={() => toggle('titleCheck')}>
                                {checklist.titleCheck ? <CheckCircle size={20} weight="fill" className="obd-sync-modal__check-icon obd-sync-modal__check-icon--done" /> : <Circle size={20} weight="regular" className="obd-sync-modal__check-icon" />}
                                <FileText size={18} weight="duotone" aria-hidden />
                                <span>Title check</span>
                            </button>
                        </li>
                        <li className="obd-sync-modal__check-item">
                            <button type="button" className="obd-sync-modal__check-row" onClick={() => toggle('creditCheck')}>
                                {checklist.creditCheck ? <CheckCircle size={20} weight="fill" className="obd-sync-modal__check-icon obd-sync-modal__check-icon--done" /> : <Circle size={20} weight="regular" className="obd-sync-modal__check-icon" />}
                                <CreditCard size={18} weight="duotone" aria-hidden />
                                <span>Credit check (IGS and LightReach)</span>
                            </button>
                        </li>
                    </ul>
                </section>
            </div>
        </DashboardModal>
    );
};

export default FinancePartnerSyncModal;
